import { useState, useEffect } from 'react';
import { validatePort, generateValidationSummary, SEVERITY } from '@/lib/portValidation';
import { loadTimezonesGeoJSON } from '@/lib/timezoneLoader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, AlertTriangle, Info, Download, Play, Loader2 } from 'lucide-react';

const PortValidator = ({ portsData, countriesData, timeZonesData }) => {
  const [ports, setPorts] = useState([]);
  const [countries, setCountries] = useState([]);
  const [timezones, setTimezones] = useState([]);
  const [timezonesGeoJSON, setTimezonesGeoJSON] = useState(null);
  
  const [validationResults, setValidationResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  const [filterSeverity, setFilterSeverity] = useState('all'); // all, errors, warnings
  const [searchTerm, setSearchTerm] = useState('');

  // Update local state when props change
  useEffect(() => {
    if (portsData && countriesData && timeZonesData) {
      setPorts(portsData);
      setCountries(countriesData);
      setTimezones(timeZonesData);
      setIsLoading(false);
      
      // Load timezone GeoJSON data
      loadTimezonesGeoJSON().then(setTimezonesGeoJSON);
    }
  }, [portsData, countriesData, timeZonesData]);

  // Legacy CSV loading removed - now uses database data via props
  const loadData = async () => {
    // Data now comes from props (portsData, countriesData, timeZonesData)
    // which are fetched from the database via API
    if (portsData && countriesData && timeZonesData) {
      setPorts(portsData);
      setCountries(countriesData);
      setTimezones(timeZonesData);

      // Load timezone GeoJSON (optional, for geographic validation)
      try {
        const geoJSON = await loadTimezonesGeoJSON();
        setTimezonesGeoJSON(geoJSON);
      } catch (err) {
        console.warn('Could not load timezone GeoJSON:', err);
      }

      console.log(`Loaded ${portsData.length} ports, ${countriesData.length} countries, ${timeZonesData.length} timezones from database`);
      setIsLoading(false);
    }
  };

  const runValidation = async () => {
    setIsValidating(true);
    setProgress(0);
    setValidationResults([]);
    setSummary(null);

    const results = [];
    const batchSize = 100;
    const totalPorts = ports.length;

    // Process in batches to allow UI updates
    for (let i = 0; i < totalPorts; i += batchSize) {
      const batch = ports.slice(i, i + batchSize);
      
      for (const port of batch) {
        const result = validatePort(
          port,
          { countries, timezones, allPorts: ports },
          timezonesGeoJSON
        );
        results.push(result);
      }

      setProgress(Math.min(100, Math.round(((i + batchSize) / totalPorts) * 100)));
      
      // Allow UI to update
      await new Promise(resolve => setTimeout(resolve, 0));
    }

    setValidationResults(results);
    const validationSummary = generateValidationSummary(results);
    setSummary(validationSummary);
    setIsValidating(false);
    setProgress(100);
  };

  const exportResults = (format = 'csv') => {
    if (format === 'csv') {
      // Flatten validation results for CSV export
      const flattenedResults = validationResults.map(result => ({
        port_id: result.port_id,
        port_name: result.port_name,
        un_locode: result.un_locode,
        is_valid: result.isValid,
        error_count: result.errors.length,
        warning_count: result.warnings.length,
        errors: result.errors.map(e => `${e.field}: ${e.message}`).join('; '),
        warnings: result.warnings.map(w => `${w.field}: ${w.message}`).join('; ')
      }));

      const csv = Papa.unparse(flattenedResults);
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `port-validation-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'json') {
      const json = JSON.stringify({
        summary,
        results: validationResults
      }, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `port-validation-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const filteredResults = validationResults.filter(result => {
    // Filter by severity
    if (filterSeverity === 'errors' && result.errors.length === 0) return false;
    if (filterSeverity === 'warnings' && result.warnings.length === 0) return false;

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        result.port_name?.toLowerCase().includes(search) ||
        result.un_locode?.toLowerCase().includes(search) ||
        result.port_id?.toString().includes(search)
      );
    }

    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading port data...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Port Data Validator</h1>
        <p className="text-muted-foreground">
          Validate port records for data accuracy, including LOCODE format, coordinates, timezone consistency, and duplicates.
        </p>
      </div>

      {/* Control Panel */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Validation Controls</CardTitle>
          <CardDescription>
            Loaded {ports.length} ports, {countries.length} countries, {timezones.length} timezones
            {timezonesGeoJSON && ` • Geographic validation enabled (${timezonesGeoJSON.features?.length} timezone boundaries)`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-center">
            <Button
              onClick={runValidation}
              disabled={isValidating || ports.length === 0}
              size="lg"
            >
              {isValidating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validating... {progress}%
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Validation
                </>
              )}
            </Button>

            {validationResults.length > 0 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => exportResults('csv')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportResults('json')}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export JSON
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Statistics */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Ports</p>
                  <p className="text-2xl font-bold">{summary.total}</p>
                </div>
                <Info className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Valid Ports</p>
                  <p className="text-2xl font-bold text-green-600">{summary.valid}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">With Errors</p>
                  <p className="text-2xl font-bold text-red-600">{summary.withErrors}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">With Warnings</p>
                  <p className="text-2xl font-bold text-yellow-600">{summary.withWarnings}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Issue Breakdown */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Errors Breakdown */}
          {Object.keys(summary.errorsByType).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-red-600">Errors by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(summary.errorsByType).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center">
                      <span className="text-sm">{type.replace(/_/g, ' ')}</span>
                      <Badge variant="destructive">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warnings Breakdown */}
          {Object.keys(summary.warningsByType).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-yellow-600">Warnings by Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(summary.warningsByType).map(([type, count]) => (
                    <div key={type} className="flex justify-between items-center">
                      <span className="text-sm">{type.replace(/_/g, ' ')}</span>
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                        {count}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Results Table */}
      {validationResults.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Validation Results</CardTitle>
            <div className="flex gap-4 items-center mt-4">
              <div className="flex gap-2">
                <Button
                  variant={filterSeverity === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterSeverity('all')}
                >
                  All ({validationResults.length})
                </Button>
                <Button
                  variant={filterSeverity === 'errors' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterSeverity('errors')}
                >
                  Errors ({summary.withErrors})
                </Button>
                <Button
                  variant={filterSeverity === 'warnings' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterSeverity('warnings')}
                >
                  Warnings ({summary.withWarnings})
                </Button>
              </div>
              <input
                type="text"
                placeholder="Search by name, LOCODE, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-4 py-2 border rounded-md"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-[600px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-background border-b">
                  <tr>
                    <th className="text-left p-2">Port ID</th>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">LOCODE</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((result, index) => (
                    <tr key={index} className="border-b hover:bg-muted/50">
                      <td className="p-2">{result.port_id}</td>
                      <td className="p-2 font-medium">{result.port_name}</td>
                      <td className="p-2">
                        {result.un_locode || <span className="text-muted-foreground">N/A</span>}
                      </td>
                      <td className="p-2">
                        {result.isValid && result.errors.length === 0 ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Valid
                          </Badge>
                        ) : result.errors.length > 0 ? (
                          <Badge variant="destructive">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {result.errors.length} Error{result.errors.length > 1 ? 's' : ''}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {result.warnings.length} Warning{result.warnings.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="space-y-1 text-sm">
                          {result.errors.map((error, i) => (
                            <div key={i} className="text-red-600">
                              <strong>{error.field}:</strong> {error.message}
                            </div>
                          ))}
                          {result.warnings.map((warning, i) => (
                            <div key={i} className="text-yellow-600">
                              <strong>{warning.field}:</strong> {warning.message}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredResults.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No results match the current filters
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PortValidator;
